// src/utilities/date-time/dates.ts
// utils/datetime.ts (Vue 3 + TypeScript, using dayjs)

import dayjs, { type Dayjs } from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import advancedFormat from 'dayjs/plugin/advancedFormat'

// dayjs plugins
dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(customParseFormat)
dayjs.extend(localizedFormat)
dayjs.extend(advancedFormat)

export const DEFAULT_DAY_START_TIME = '00:00:00'
export const DEFAULT_DAY_END_TIME = '23:59:59'
const DATE_FORMAT_NO_TIMEZONE = 'YYYY/MM/DD HH:mm:ss'

/** Return a parser that treats input as local or UTC */
export function getParser(local = false) {
  return (input?: dayjs.ConfigType) => (local ? dayjs(input) : dayjs.utc(input))
}

/** Only accepts 24h "HH:mm" (or "HH:mm:ss") */
export function isValidTime(str: string): boolean {
  return dayjs(str, 'HH:mm', true).isValid() || dayjs(str, 'HH:mm:ss', true).isValid()
}

/**
 * Given a date object, format datetime in UTC
 * given: Tue Oct 09 2018 00:00:00 GMT-0700
 * returns: "2018-10-09T07:00:00"
 */
export function getUtcDateString(dateObj: dayjs.ConfigType): string {
  return dayjs.utc(dateObj).format('YYYY-MM-DD[T]HH:mm:ss')
}

/**
 * Given a date field from a table row, return a UNIX timestamp (seconds)
 * given: '2024-04-01T20:15:18+00:00' -> 1712002518
 */
export function getTimeStampFromTableDateField(date: string | undefined): number | undefined {
  if (date == null) return undefined
  if (typeof date === 'number') return date

  const ts = new Date(date as string).getTime()
  if (isNaN(ts)) throw new Error('Invalid timestamp: NaN')
  return Math.floor(ts / 1000)
}

export function getFormattedDate(
  dateObj: dayjs.ConfigType,
  format: string,
  { local }: { local?: boolean } = {}
): string {
  return getParser(!!local)(dateObj).format(format)
}

/** Returns user timezone from localStorage or falls back to detected timezone */
export function getUserTimezone(): string {
  // Try to get from userSettingsLK (stored by UserSettingsStore)
  try {
    const userSettings = JSON.parse(localStorage.getItem('userSettingsLK') ?? '{}')
    if (userSettings.timezone) {
      return userSettings.timezone
    }
  } catch {
    // Ignore parse errors
  }

  // Fallback to TZ localStorage (set in main.js)
  const storedTz = localStorage.getItem('TZ')
  if (storedTz) {
    return storedTz
  }

  // Final fallback to detected timezone
  return dayjs.tz.guess()
}

/** Given a UTC date, return a Date object in local time */
export function getUtcToLocalDateObject(date: dayjs.ConfigType): Date {
  return dayjs.utc(date).local().toDate()
}

/**
 * Given a UTC timestamp, return a system date object with the same local wall time.
 * Strips timezone when formatting from UTC.
 */
export function getUtcToSystem(dateObj: dayjs.ConfigType): Date {
  return new Date(dayjs.utc(dateObj).format(DATE_FORMAT_NO_TIMEZONE))
}

/**
 * Given a timestamp, format to local preference, then strip timezone to a system Date.
 */
export function getLocalToSystem(dateObj: dayjs.ConfigType): Date {
  return new Date(dayjs(dateObj).format(DATE_FORMAT_NO_TIMEZONE))
}

/** Get the beginning of day (midnight, local) */
function getStartOfDay(date: dayjs.ConfigType): Date {
  return dayjs(date).startOf('day').local().toDate()
}

/** Get end of day (inclusive: 23:59:59, local) */
export function getEndOfDay(date: dayjs.ConfigType): Date {
  return dayjs(date).endOf('day').local().toDate()
}

export function getPeriodAgo(period: dayjs.ManipulateType, unit: number): Dayjs {
  return dayjs().local().subtract(unit, period)
}

/** Start of the day for a period ago (e.g. 2 weeks ago at midnight) */
export function getStartOfPeriodAgo(period: dayjs.ManipulateType, unit: number): Date {
  return getStartOfDay(getPeriodAgo(period, unit))
}

/** Does the user prefer a 24 hour clock? */
export function shouldUse24Hours(): boolean {
  // Try to get from userSettingsLK (stored by UserSettingsStore)
  try {
    const userSettings = JSON.parse(localStorage.getItem('userSettingsLK') ?? '{}')
    if (userSettings.clock24Hours !== undefined) {
      return Boolean(userSettings.clock24Hours)
    }
  } catch {
    // Ignore parse errors
  }

  // Default to false (12-hour format)
  return false
}

/** Common date format */
function getDateFormat({ year }: { year?: boolean }) {
  // "Jan 1, 2022" or "Jan 1"
  return year ? 'MMM D, YYYY' : 'MMM D'
}

/**
 * Common time format
 * Note: dayjs doesn't support timezone abbreviations like "z" out of the box.
 * We use numeric offset (ZZ). If you truly need abbreviations, you'd need a custom formatter.
 */
export function getTimeFormat({
  clock24Hours,
  seconds,
  timeZone,
  milliseconds
}: {
  clock24Hours?: boolean
  milliseconds?: boolean
  seconds?: boolean
  timeZone?: boolean
} = {}) {
  let format = ''

  if (clock24Hours ?? shouldUse24Hours()) {
    format = milliseconds ? 'HH:mm:ss.SSS' : seconds ? 'HH:mm:ss' : 'HH:mm'
  } else {
    // Requires localizedFormat plugin for LT/LTS
    format = milliseconds ? 'h:mm:ss.SSS A' : seconds ? 'LTS' : 'LT'
  }

  if (timeZone) {
    // Use numeric offset; e.g. +01:00
    format += ' ZZ'
  }

  return format
}

interface FormatProps {
  clock24Hours?: boolean
  dateOnly?: boolean
  milliseconds?: boolean
  seconds?: boolean
  timeOnly?: boolean
  timeZone?: boolean
  /**
   * Whether to show the year. If not specified, the returned date string will
   * not contain the year if the date is within the current calendar year.
   */
  year?: boolean
}

export function getFormat({
  dateOnly,
  timeOnly,
  year,
  seconds,
  timeZone,
  clock24Hours,
  milliseconds
}: FormatProps = {}) {
  if (dateOnly) return getDateFormat({ year })
  if (timeOnly) return getTimeFormat({ clock24Hours, seconds, timeZone })

  const dateFormat = getDateFormat({ year })
  const timeFormat = getTimeFormat({ clock24Hours, seconds, milliseconds, timeZone })

  // If year is shown, dateFormat already has a comma
  return year ? `${dateFormat} ${timeFormat}` : `${dateFormat}, ${timeFormat}`
}

export function getInternalDate(date: string | Date, utc?: boolean | null) {
  if (utc) return getUtcToSystem(date)
  const tz = getUserTimezone()
  // take the UTC instant, represent it in user's TZ, then strip TZ to a system Date
  return new Date(dayjs.utc(date).tz(tz).format(DATE_FORMAT_NO_TIMEZONE))
}

/**
 * Strips timezone from local date, applies a timezone, returns UTC Date
 */
export function getDateWithTimezoneInUtc(date?: Date, utc?: boolean | null) {
  const tz = utc ? 'UTC' : getUserTimezone()
  const localWall = dayjs(date).local().format('YYYY-MM-DD HH:mm:ss')
  return dayjs.tz(localWall, tz).utc().toDate()
}

/** Converts a string or timestamp in ms to a Date */
export function getDateFromTimestamp(value: unknown): Date | null {
  if (typeof value !== 'string' && typeof value !== 'number') return null
  const d = new Date(value)
  return isNaN(d.getTime()) ? null : d
}

/**
 * Set time (HH:mm or HH:mm:ss) on a given Date (local or UTC)
 */
export function setDateToTime(dateObj: string | Date, timeStr: string, { local }: { local?: boolean } = {}): Date {
  const [hours, minutes, seconds] = timeStr.split(':').map(t => parseInt(t, 10)) as [number, number, number | undefined]

  const d = new Date(+new Date(dateObj))

  if (local) {
    d.setHours(hours, minutes, seconds ?? d.getSeconds(), d.getMilliseconds())
  } else {
    d.setUTCHours(hours, minutes, seconds ?? d.getUTCSeconds(), d.getUTCMilliseconds())
  }

  return d
}
